import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import mapboxgl from 'mapbox-gl';
import { MAPBOX_TOKEN } from '../../../utils/constants';
import { createCircle } from '../../map/utils/geometryUtils';
import './FindClubMapView.css';

if (typeof mapboxgl !== 'undefined' && MAPBOX_TOKEN && !mapboxgl.accessToken) {
  mapboxgl.accessToken = MAPBOX_TOKEN;
}

const RADIUS_SOURCE_ID = 'find-club-radius';
const RADIUS_FILL_LAYER_ID = 'find-club-radius-fill';
const RADIUS_LINE_LAYER_ID = 'find-club-radius-line';

function milesToYards(miles) {
  return Math.max(0, Number(miles) || 0) * 1760;
}

function clubCoordinates(club) {
  const lat = club?.approx_lat;
  const lng = club?.approx_lng;
  if (typeof lat !== 'number' || typeof lng !== 'number') return null;
  return { latitude: lat, longitude: lng };
}

export function FindClubMapView({
  center,
  radiusMiles,
  clubs,
  activeClubId = null,
  onClubSelect,
}) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const centerMarkerRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return undefined;
    if (typeof window === 'undefined' || !MAPBOX_TOKEN || !mapboxgl?.Map) return undefined;

    const map = new mapboxgl.Map({
      container: containerRef.current,
      style: 'mapbox://styles/mapbox/outdoors-v12',
      center: [center.longitude, center.latitude],
      zoom: 8,
      attributionControl: false,
    });
    mapRef.current = map;

    map.on('load', () => {
      map.addSource(RADIUS_SOURCE_ID, {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      });
      map.addLayer({
        id: RADIUS_FILL_LAYER_ID,
        type: 'fill',
        source: RADIUS_SOURCE_ID,
        paint: {
          'fill-color': '#2f6f4f',
          'fill-opacity': 0.08,
        },
      });
      map.addLayer({
        id: RADIUS_LINE_LAYER_ID,
        type: 'line',
        source: RADIUS_SOURCE_ID,
        paint: {
          'line-color': '#2f6f4f',
          'line-width': 2,
          'line-dasharray': [2, 2],
          'line-opacity': 0.75,
        },
      });
    });

    return () => {
      markersRef.current.forEach((marker) => marker.remove());
      markersRef.current = [];
      centerMarkerRef.current?.remove();
      centerMarkerRef.current = null;
      map.remove();
      mapRef.current = null;
    };
  }, [center.latitude, center.longitude]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return undefined;

    const updateMap = () => {
      const circle = createCircle(center.longitude, center.latitude, milesToYards(radiusMiles));
      const radiusSource = map.getSource(RADIUS_SOURCE_ID);
      if (radiusSource && circle) {
        radiusSource.setData({
          type: 'Feature',
          geometry: circle,
          properties: {},
        });
      }

      markersRef.current.forEach((marker) => marker.remove());
      markersRef.current = [];

      centerMarkerRef.current?.remove();
      const centerEl = document.createElement('div');
      centerEl.className = 'find-club-map__center-marker';
      centerEl.setAttribute('aria-hidden', 'true');
      centerMarkerRef.current = new mapboxgl.Marker(centerEl)
        .setLngLat([center.longitude, center.latitude])
        .addTo(map);

      const bounds = new mapboxgl.LngLatBounds();
      bounds.extend([center.longitude, center.latitude]);

      clubs.forEach((club) => {
        const coords = clubCoordinates(club);
        if (!coords) return;

        const el = document.createElement('button');
        el.type = 'button';
        el.className = 'find-club-map__pin';
        if (club.is_promoted) el.classList.add('find-club-map__pin--promoted');
        if (activeClubId === club.id) el.classList.add('find-club-map__pin--active');
        el.setAttribute('aria-label', club.name);
        el.title = club.name;
        el.addEventListener('click', (event) => {
          event.stopPropagation();
          onClubSelect?.(club.id);
        });

        const marker = new mapboxgl.Marker(el)
          .setLngLat([coords.longitude, coords.latitude])
          .addTo(map);
        markersRef.current.push(marker);
        bounds.extend([coords.longitude, coords.latitude]);
      });

      if (circle?.coordinates?.[0]?.length) {
        circle.coordinates[0].forEach(([lng, lat]) => bounds.extend([lng, lat]));
      }

      map.fitBounds(bounds, { padding: 48, duration: 500, maxZoom: 11 });
    };

    if (map.isStyleLoaded()) {
      updateMap();
      return undefined;
    }

    map.once('load', updateMap);
    return undefined;
  }, [activeClubId, center.latitude, center.longitude, clubs, onClubSelect, radiusMiles]);

  if (!MAPBOX_TOKEN) {
    return (
      <div className="find-club-map find-club-map--unavailable" data-testid="find-club-map-unavailable">
        Map preview is unavailable right now.
      </div>
    );
  }

  return (
    <div className="find-club-map" data-testid="find-club-map">
      <div className="find-club-map__canvas" ref={containerRef} />
      <div className="find-club-map__legend" aria-hidden>
        <span><i className="find-club-map__dot find-club-map__dot--you" /> You</span>
        <span><i className="find-club-map__dot find-club-map__dot--club" /> In range</span>
        <span><i className="find-club-map__dot find-club-map__dot--promoted" /> Promoted</span>
      </div>
      <span className="find-club-map__radius-label">{radiusMiles} mi radius</span>
      <span className="find-club-map__privacy-note">Approximate locations only</span>
    </div>
  );
}

FindClubMapView.propTypes = {
  center: PropTypes.shape({
    latitude: PropTypes.number.isRequired,
    longitude: PropTypes.number.isRequired,
  }).isRequired,
  radiusMiles: PropTypes.number.isRequired,
  clubs: PropTypes.arrayOf(PropTypes.object).isRequired,
  activeClubId: PropTypes.number,
  onClubSelect: PropTypes.func,
};
